import { useState, useCallback } from 'react'
import { SavedFilter, SavedFilterData } from '../types'

const getCSRFToken = (): string => {
  return document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || ''
}

interface UseSavedFiltersProps {
  initialFilters: SavedFilter[]
  projectGroupId: number
}

interface UseSavedFiltersResult {
  savedFilters: SavedFilter[]
  createFilter: (name: string, filter: SavedFilterData, isDefault: boolean) => Promise<SavedFilter>
  updateFilter: (id: number, name: string, filter: SavedFilterData, isDefault: boolean) => Promise<SavedFilter>
  deleteFilter: (id: number) => Promise<void>
  isLoading: boolean
}

export function useSavedFilters({ initialFilters, projectGroupId }: UseSavedFiltersProps): UseSavedFiltersResult {
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>(initialFilters)
  const [isLoading, setIsLoading] = useState(false)

  const createFilter = useCallback(async (name: string, filter: SavedFilterData, isDefault: boolean): Promise<SavedFilter> => {
    setIsLoading(true)
    try {
      const response = await fetch('/saved-filters', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
        body: JSON.stringify({
          saved_filter: {
            name,
            filter,
            is_default: isDefault,
            type: 'SavedFilters::ProjectsOverview',
            scope_type: 'ProjectGroup',
            scope_id: projectGroupId,
          },
        }),
      })

      const data = await response.json()
      if (!data.success) {
        throw new Error(data.errors?.name?.[0] || 'Failed to create filter')
      }

      const newFilter = data.saved_filter as SavedFilter
      setSavedFilters((prev) => {
        // If the new filter is default, unset default on others
        if (newFilter.is_default) {
          return [...prev.map((f) => ({ ...f, is_default: false })), newFilter]
        }
        return [...prev, newFilter]
      })
      return newFilter
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updateFilter = useCallback(async (id: number, name: string, filter: SavedFilterData, isDefault: boolean): Promise<SavedFilter> => {
    setIsLoading(true)
    try {
      const response = await fetch(`/saved-filters/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
        body: JSON.stringify({
          saved_filter: {
            name,
            filter,
            is_default: isDefault,
          },
        }),
      })

      const data = await response.json()
      if (!data.success) {
        throw new Error(data.errors?.name?.[0] || 'Failed to update filter')
      }

      const updatedFilter = data.saved_filter as SavedFilter
      setSavedFilters((prev) => {
        return prev.map((f) => {
          if (f.id === id) {
            return updatedFilter
          }
          // If the updated filter is now default, unset default on others
          if (updatedFilter.is_default && f.is_default) {
            return { ...f, is_default: false }
          }
          return f
        })
      })
      return updatedFilter
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deleteFilter = useCallback(async (id: number): Promise<void> => {
    setIsLoading(true)
    try {
      const response = await fetch(`/saved-filters/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
      })

      const data = await response.json()
      if (!data.success) {
        throw new Error('Failed to delete filter')
      }

      setSavedFilters((prev) => prev.filter((f) => f.id !== id))
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    savedFilters,
    createFilter,
    updateFilter,
    deleteFilter,
    isLoading,
  }
}
